import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useStoredState } from '$components'
import { StoredState } from 'src/enums/stored-state'
import { useAuth } from '$features/auth/hooks/use-auth'
import { tutorialCompleteMutation, authUserQueryKey } from '$api-client/@tanstack/react-query.gen'
import { ToastHelper } from '$components'
import { Analytics, AnalyticsEvent } from '$utils'
import type { OnboardingWizardStep, OnboardingWizardState, OnboardingWizardActions, OnboardingWizardConfig } from '../types'

export function useOnboardingTutorial(config: OnboardingWizardConfig) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { user } = useAuth()
  const [currentStepIndex, setCurrentStepIndex] = useStoredState(StoredState.OnboardingWizardStep, 0)
  const [isVisible, setIsVisible] = useStoredState(StoredState.OnboardingWizardVisible, true)
  const [isCollapsed, setIsCollapsed] = useStoredState(StoredState.OnboardingWizardCollapsed, false)

  // Check if user has already completed the tutorial
  const isTutorialCompleted = !!user?.tutorial_completed_at

  // Mutation to mark tutorial as completed
  const { mutate: completeTutorial, isPending: isCompletingTutorial } = useMutation({
    ...tutorialCompleteMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: authUserQueryKey(),
      })
      ToastHelper.success('Tutorial completed successfully!')
      setIsVisible(false)
      Analytics.trackEvent(AnalyticsEvent.OnboardingCompleted)
    },
    onError: () => ToastHelper.error('Failed to complete tutorial.'),
  })

  // Initialize steps with completion status
  const [steps, setSteps] = useState<OnboardingWizardStep[]>(() =>
    config.steps.map((step, index) => ({
      ...step,
      isCompleted: false,
      isActive: index === 0,
    })),
  )

  // Update active step based on current step index
  useEffect(() => {
    setSteps(prevSteps =>
      prevSteps.map((step, index) => ({
        ...step,
        isActive: index === currentStepIndex,
      })),
    )
  }, [currentStepIndex])

  // Navigation actions
  const goToStep = useCallback((stepIndex: number) => {
    if (stepIndex >= 0 && stepIndex < steps.length) {
      setCurrentStepIndex(stepIndex)
      const step = steps[stepIndex]
      if (step.route) {
        navigate(step.route)
      }
    }
  }, [steps, setCurrentStepIndex, navigate])

  const nextStep = useCallback(() => {
    if (currentStepIndex < steps.length - 1) {
      goToStep(currentStepIndex + 1)
    }
  }, [currentStepIndex, steps.length, goToStep])

  const previousStep = useCallback(() => {
    if (currentStepIndex > 0) {
      goToStep(currentStepIndex - 1)
    }
  }, [currentStepIndex, goToStep])

  const completeStep = useCallback((stepIndex: number) => {
    setSteps(prevSteps =>
      prevSteps.map((step, index) =>
        index === stepIndex ? { ...step, isCompleted: true } : step,
      ),
    )
  }, [])

  const toggleVisibility = useCallback(() => {
    setIsVisible(prev => !prev)
  }, [setIsVisible])

  const toggleCollapsed = useCallback(() => {
    setIsCollapsed(prev => !prev)
  }, [setIsCollapsed])

  const resetWizard = useCallback(() => {
    setCurrentStepIndex(0)
    setSteps(config.steps.map((step, index) => ({
      ...step,
      isCompleted: false,
      isActive: index === 0,
    })))
    setIsVisible(true)
    setIsCollapsed(false)
  }, [config.steps, setCurrentStepIndex, setIsVisible, setIsCollapsed])

  const completeTutorialFlow = useCallback(() => {
    completeTutorial({})
  }, [completeTutorial])

  // Computed values
  const currentStep = steps[currentStepIndex]
  const isFirstStep = currentStepIndex === 0
  const isLastStep = currentStepIndex === steps.length - 1
  const completedStepsCount = steps.filter(step => step.isCompleted).length
  const progressPercentage = (completedStepsCount / steps.length) * 100

  const state: OnboardingWizardState = {
    currentStepIndex,
    steps,
    isVisible,
    isCollapsed,
  }

  const actions: OnboardingWizardActions = {
    goToStep,
    nextStep,
    previousStep,
    toggleVisibility,
    toggleCollapsed,
    completeStep,
    resetWizard,
  }

  return {
    ...state,
    ...actions,
    currentStep,
    isFirstStep,
    isLastStep,
    completedStepsCount,
    progressPercentage,
    isTutorialCompleted,
    completeTutorialFlow,
    isCompletingTutorial,
  }
}
